/* eslint-disable react/prop-types */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import dayjs from 'dayjs';
import {
  Grid, Card, Box, Typography, Chip, MenuItem, Select,
  FormControl, InputLabel, Dialog, DialogTitle, DialogContent,
  DialogActions, IconButton, Divider, Tooltip, Alert,
  TablePagination, Paper, Stack, Button, Switch, Collapse,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import EventIcon from '@mui/icons-material/Event';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import NotesIcon from '@mui/icons-material/Notes';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import PhoneIcon from '@mui/icons-material/Phone';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HistoryIcon from '@mui/icons-material/History';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import Swal from 'sweetalert2';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import MDDatePicker from 'components/MDDatePicker';
import MDTimePicker from 'components/MDTimePicker';
import RichTextEditor from 'components/RichTextEditor';
import LoaderOverlay from 'components/LoaderOverlay';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';
import FollowUpDialog from 'components/FollowUpDialog';
import { api } from '../../api';

const TEAL = '#00798E';
const TEAL_LIGHT = 'rgba(0, 121, 142, 0.07)';
const TEAL_BORDER = 'rgba(0, 121, 142, 0.28)';

const STATUS_LABELS ={ PENDING: 'Pending', ATTENDED: 'Attended', RESCHEDULED: 'Rescheduled', CLOSED: 'Closed' };
const STATUS_LIST = ['PENDING', 'ATTENDED', 'RESCHEDULED', 'CLOSED'];

// Colour for the compact status select border/text
const STATUS_HEX = { PENDING: '#ed6c02', ATTENDED: '#2e7d32', RESCHEDULED: '#0288d1', CLOSED: '#d32f2f' };

const ROWS_PER_PAGE_OPTIONS = [10, 20, 50];

/* ── helpers ─────────────────────────────────────────────────────────────── */
function fmt(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}
function fmtFull(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' });
}
function offsetDate(days) {
  const d = new Date(); d.setDate(d.getDate() + days); return d.toISOString().split('T')[0];
}
// Local (browser-timezone) YYYY-MM-DD — offsetDate()/toISOString() converts
// through UTC first, which silently shifts "today" to the wrong calendar
// date for any timezone ahead of UTC during early-morning local hours.
function localDateStr(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0'); // eslint-disable-line no-magic-numbers
  const d = String(date.getDate()).padStart(2, '0'); // eslint-disable-line no-magic-numbers
  return `${y}-${m}-${d}`;
}
function stripHtml(h) {
  if (!h) return ''; return h.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
}
function resolvePatientName(ci, id) {
  if (!ci) return `Patient #${id}`;
  const c = ci.contact || ci;
  return c?.full_name || [c?.first_name, c?.middle_name, c?.last_name].filter(Boolean).join(' ') || `Patient #${id}`;
}
function resolvePhone(ci) { return ci?.phones?.[0]?.phone_number || null; }
// doctorInfo = { name, occupation, mobile_number } from v1:get-doctor-by-user-id
function resolveDoctorName(di) {
  if (!di) return null;
  return di.name || di.user_name || di.full_name || null;
}
function resolveDoctorOccupation(di) {
  return di?.occupation || null;
}

/* ── Swal helpers ─────────────────────────────────────────────────────────── */
async function swalConfirm({ title, html, confirmText = 'Yes, Confirm' }) {
  return Swal.fire({
    title, html, icon: 'question',
    showCancelButton: true,
    confirmButtonColor: TEAL, cancelButtonColor: '#9e9e9e',
    confirmButtonText: confirmText, cancelButtonText: 'Cancel',
    customClass: { confirmButton: 'swal-btn-white' },
  });
}
async function swalToast(title, text, icon = 'success') {
  return Swal.fire({
    title, text, icon,
    toast: true, position: 'top-end',
    showConfirmButton: false,
    timer: 2200, timerProgressBar: true,
  });
}

/* ── Inline history ──────────────────────────────────────────────────────── */
function InlineHistory({ logs }) {
  const entries = (logs || []).filter(l => l.change_type !== 'NOTE_ADDED');
  if (!entries.length) return null;
  return (
    <Box sx={{ pl: 1.5, borderLeft: `2px solid ${TEAL_BORDER}`, mt: 0.75 }}>
      {entries.map((log, i) => (
        <Box key={log.id || i} display="flex" alignItems="baseline" gap={0.75} sx={{ mb: 0.2 }}>
          <Typography variant="caption" sx={{ color: '#bbb', whiteSpace: 'nowrap', fontSize: '0.64rem' }}>{fmt(log.created_at)}</Typography>
          <Typography variant="caption" sx={{ color: TEAL, fontWeight: 700, fontSize: '0.63rem', textTransform: 'uppercase' }}>
            {log.change_type?.replace(/_/g, ' ')}
          </Typography>
          <Typography variant="caption" sx={{ color: '#555', fontSize: '0.69rem' }}>
            {log.change_type === 'DATE_CHANGED' && (
              <><span style={{ textDecoration: 'line-through', color: '#ccc', marginRight: 3 }}>{fmt(log.old_date)}</span>→ <strong>{fmt(log.new_date)}</strong></>
            )}
            {log.change_type === 'STATUS_CHANGED' && (
              <><span style={{ textDecoration: 'line-through', color: '#ccc', marginRight: 3 }}>{log.old_status}</span>→ <strong>{log.new_status}</strong></>
            )}
            {log.change_type === 'CREATED' && (
              <>Date: <strong>{fmt(log.new_date)}</strong>{log.new_status && <> · <strong>{log.new_status}</strong></>}</>
            )}
            {log.notes && log.change_type !== 'NOTE_ADDED' && (
              <span style={{ color: '#999', marginLeft: 5 }}>· {stripHtml(log.notes)}</span>
            )}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

function NotesList({ logs }) {
  const notes = (logs || []).filter(l => l.change_type === 'NOTE_ADDED');
  if (!notes.length) return null;
  return (
    <Box sx={{ mt: 0.5 }}>
      <Typography variant="caption" sx={{ color: '#aaa', fontWeight: 700, textTransform: 'uppercase', fontSize: '0.6rem', letterSpacing: '0.06em' }}>Notes</Typography>
      {notes.map((n, i) => (
        <Box key={n.id || i} display="flex" gap={0.75} alignItems="baseline" sx={{ mt: 0.15 }}>
          <Typography variant="caption" sx={{ color: '#bbb', whiteSpace: 'nowrap', fontSize: '0.64rem' }}>{fmt(n.created_at)}</Typography>
          <Typography variant="caption" sx={{ color: '#444', fontSize: '0.7rem', fontStyle: 'italic' }}>{stripHtml(n.notes)}</Typography>
        </Box>
      ))}
    </Box>
  );
}

/* ── Compact follow-up row ───────────────────────────────────────────────── */
function FollowupRow({ followup, onEdit, onAddNote, onNextFollowUp, onViewDetails, onCreateAppointment, onRefresh, currentUserId }) {
  const [closing, setClosing] = useState(false);
  const [logsOpen, setLogsOpen] = useState(false);
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [newDate, setNewDate] = useState('');
  const [savingDate, setSavingDate] = useState(false);
  const [savingStatus, setSavingStatus] = useState(false);

  const patientName = resolvePatientName(followup.contactInfo, followup.irm_contacts_id);
  const phone = resolvePhone(followup.contactInfo);
  const doctorName = resolveDoctorName(followup.doctorInfo);
  const doctorOccupation = resolveDoctorOccupation(followup.doctorInfo);
  const reason = followup.reason ? stripHtml(followup.reason) : null;
  const treatment = followup.treatment_line ? stripHtml(followup.treatment_line) : null;
  const statusColor = STATUS_HEX[followup.status] || TEAL;

  const handleQuickStatus = async (status) => {
    if (status === followup.status || savingStatus) return;
    const res = await swalConfirm({
      title: 'Update Status',
      html: `Change status from <strong>${STATUS_LABELS[followup.status]}</strong> to <strong style="color:${STATUS_HEX[status]}">${STATUS_LABELS[status]}</strong>?`,
      confirmText: 'Yes, Update',
    });
    if (!res.isConfirmed) return;
    setSavingStatus(true);
    try {
      const resp = await api.opd.updateFollowup(followup.id, { status, updated_by: currentUserId });
      if (resp?.flag === 1 || resp?.statusCode === 200) {
        if (status === 'CLOSED') {
          setClosing(true);
          await swalToast('Closed', `Follow-up marked as Closed.`, 'success');
          setTimeout(() => onRefresh(), 480);
        } else {
          await swalToast('Status Updated', `Status changed to ${STATUS_LABELS[status]}.`);
          onRefresh();
        }
      }
    } catch { /**/ } finally { setSavingStatus(false); }
  };

  const handleSaveDate = async () => {
    const today = new Date().toISOString().split('T')[0];
    if (!newDate) { Swal.fire({ icon: 'warning', title: 'Select a date', timer: 1400, showConfirmButton: false }); return; }
    if (newDate <= today) { Swal.fire({ icon: 'warning', title: 'Date must be future', timer: 1600, showConfirmButton: false }); return; }
    const res = await swalConfirm({
      title: 'Confirm Reschedule',
      html: `Reschedule to <strong style="color:${TEAL}">${fmt(newDate)}</strong>?<br><small style="color:#888">Status will be set to <em>Rescheduled</em></small>`,
      confirmText: 'Yes, Reschedule',
    });
    if (!res.isConfirmed) return;
    setSavingDate(true);
    try {
      const resp = await api.opd.updateFollowup(followup.id, { probable_date: newDate, status: 'RESCHEDULED', updated_by: currentUserId });
      if (resp?.flag === 1 || resp?.statusCode === 200) {
        setRescheduleOpen(false); setNewDate('');
        await swalToast('Rescheduled', `New date: ${fmt(newDate)}`);
        onRefresh();
      }
    } catch { /**/ } finally { setSavingDate(false); }
  };

  const minDate = useMemo(() => dayjs().add(1, 'day'), []);

  return (
    <Paper
      variant="outlined"
      sx={{
        mb: 0.75,
        borderLeft: `3px solid ${statusColor}`,
        borderRadius: 1.5,
        overflow: 'hidden',
        transition: 'opacity 0.42s ease, transform 0.42s ease, max-height 0.5s ease, margin-bottom 0.5s ease, box-shadow 0.15s',
        maxHeight: closing ? 0 : 1000,
        opacity: closing ? 0 : 1,
        transform: closing ? 'translateX(40px) scaleY(0.85)' : 'none',
        marginBottom: closing ? '0 !important' : undefined,
        '&:hover': { boxShadow: '0 1px 8px rgba(0,121,142,0.1)', borderColor: statusColor },
      }}
    >
      <Box sx={{ px: 1.75, py: 1.1, display: 'flex', alignItems: 'center', gap: 1.5 }}>

        {/* LEFT — patient + doctor + reason */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box display="flex" alignItems="center" gap={0.75} flexWrap="wrap">
            <Chip
              label={STATUS_LABELS[followup.status] || followup.status}
              size="small"
              sx={{
                height: 17, fontSize: '0.6rem', fontWeight: 700,
                bgcolor: `${statusColor}18`, color: statusColor,
                border: `1px solid ${statusColor}44`,
                '& .MuiChip-label': { px: 0.7 },
              }}
            />
            <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.83rem', color: '#222' }}>
              {patientName}
            </Typography>
            {phone && (
              <Box display="flex" alignItems="center" gap={0.25}>
                <PhoneIcon sx={{ fontSize: 10, color: '#ccc' }} />
                <Typography variant="caption" sx={{ color: '#999', fontSize: '0.68rem' }}>{phone}</Typography>
              </Box>
            )}
            {(doctorName || doctorOccupation) && (
              <Box display="flex" alignItems="center" gap={0.3}>
                <LocalHospitalIcon sx={{ fontSize: 10, color: TEAL }} />
                <Typography variant="caption" sx={{ color: '#666', fontSize: '0.68rem' }}>
                  {doctorName}
                  {doctorOccupation && <span style={{ color: '#aaa', marginLeft: 2 }}>({doctorOccupation})</span>}
                </Typography>
              </Box>
            )}
          </Box>

          {(reason || treatment) && (
            <Typography variant="caption" sx={{ display: 'block', color: '#888', fontSize: '0.68rem', lineHeight: 1.5, mt: 0.35, pl: 0.25 }}>
              {reason && <><span style={{ color: '#555', fontWeight: 600 }}>Reason:</span> {reason}</>}
              {reason && treatment && <span style={{ color: '#ddd', margin: '0 5px' }}>|</span>}
              {treatment && <><span style={{ color: '#555', fontWeight: 600 }}>Tx:</span> {treatment.length > 80 ? `${treatment.slice(0, 80)}…` : treatment}</>}
            </Typography>
          )}
        </Box>

        {/* RIGHT — actions */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.6, flexShrink: 0 }}>
          {/* Status select + icon actions */}
          <Box display="flex" alignItems="center" gap={0.5}>
            <Select
              value={followup.status}
              onChange={e => handleQuickStatus(e.target.value)}
              disabled={savingStatus}
              size="small"
              variant="outlined"
              sx={{
                height: 24, fontSize: '0.64rem', fontWeight: 700,
                color: `${statusColor} !important`,
                '& .MuiSelect-select': { py: 0, px: '7px !important', display: 'flex', alignItems: 'center' },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: `${statusColor}66`, borderWidth: '1.5px' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: statusColor },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: statusColor },
                '& .MuiSvgIcon-root': { fontSize: 13, color: statusColor },
              }}
            >
              {STATUS_LIST.map(s => (
                <MenuItem key={s} value={s} sx={{ fontSize: '0.7rem', color: STATUS_HEX[s], fontWeight: s === followup.status ? 700 : 400 }}>
                  {STATUS_LABELS[s]}
                </MenuItem>
              ))}
            </Select>

            <Box sx={{ width: 1, height: 14, bgcolor: '#e8e8e8', mx: 0.2 }} />

            <Tooltip title="View Details">
              <IconButton size="small" onClick={() => onViewDetails(followup)} sx={{ color: '#7b61ff', p: 0.35 }}>
                <VisibilityIcon sx={{ fontSize: 13 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Edit">
              <IconButton size="small" onClick={() => onEdit(followup)} sx={{ color: TEAL, p: 0.35 }}>
                <EditIcon sx={{ fontSize: 13 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Add Note">
              <IconButton size="small" onClick={() => onAddNote(followup)} sx={{ color: '#bbb', p: 0.35, '&:hover': { color: '#666' } }}>
                <NoteAddIcon sx={{ fontSize: 13 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Schedule Next Follow-Up">
              <IconButton size="small" onClick={() => onNextFollowUp(followup)} sx={{ color: TEAL, p: 0.35 }}>
                <EventRepeatIcon sx={{ fontSize: 13 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Create Appointment">
              <IconButton size="small" onClick={() => onCreateAppointment(followup)} sx={{ color: '#2e7d32', p: 0.35 }}>
                <EventAvailableIcon sx={{ fontSize: 13 }} />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Reschedule — inline single liner */}
          {!rescheduleOpen ? (
            <Typography
              variant="caption"
              onClick={() => { setNewDate(''); setRescheduleOpen(true); }}
              sx={{
                color: '#ccc', cursor: 'pointer', fontSize: '0.62rem',
                display: 'flex', alignItems: 'center', gap: 0.35,
                '&:hover': { color: TEAL },
                transition: 'color 0.15s',
              }}
            >
              <EventIcon sx={{ fontSize: 10 }} /> Reschedule
            </Typography>
          ) : (
            <Box display="flex" alignItems="center" gap={0.6}>
              <MDDatePicker
                label=""
                value={newDate}
                onChange={d => setNewDate(d)}
                minDate={minDate}
                textFieldProps={{
                  size: 'small',
                  placeholder: 'DD/MM/YYYY',
                  sx: {
                    width: 140,
                    '& .MuiOutlinedInput-root': {
                      height: 26,
                      fontSize: '0.7rem',
                      '& fieldset': { borderColor: TEAL_BORDER },
                      '&:hover fieldset': { borderColor: TEAL },
                      '&.Mui-focused fieldset': { borderColor: TEAL },
                    },
                    '& input': { py: '3px', px: '7px' },
                  },
                }}
              />
              <Button
                size="small" variant="contained"
                onClick={handleSaveDate} disabled={savingDate}
                sx={{
                  height: 26, minWidth: 40, px: 0.75, py: 0,
                  fontSize: '0.63rem', fontWeight: 700,
                  bgcolor: TEAL, color: 'white !important',
                  '&:hover': { bgcolor: '#005f6e' },
                  '&:disabled': { bgcolor: '#b0d4da', color: 'white !important' },
                  boxShadow: 'none',
                }}
              >
                {savingDate ? '…' : 'Save'}
              </Button>
              <Typography
                variant="caption"
                onClick={() => setRescheduleOpen(false)}
                sx={{ color: '#ccc', cursor: 'pointer', fontSize: '0.7rem', lineHeight: 1, '&:hover': { color: '#999' } }}
              >
                ✕
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* ── Collapsible change log ── */}
      {(followup.logs?.length > 0) && (
        <>
          <Box
            onClick={() => setLogsOpen(o => !o)}
            sx={{
              display: 'flex', alignItems: 'center', gap: 0.5,
              px: 1.75, py: 0.4,
              bgcolor: logsOpen ? `${TEAL}08` : 'transparent',
              borderTop: `1px dashed ${logsOpen ? TEAL_BORDER : '#f0f0f0'}`,
              cursor: 'pointer',
              transition: 'background-color 0.18s',
              '&:hover': { bgcolor: `${TEAL}08` },
            }}
          >
            <HistoryIcon sx={{ fontSize: 11, color: logsOpen ? TEAL : '#ccc' }} />
            <Typography variant="caption" sx={{ fontSize: '0.62rem', color: logsOpen ? TEAL : '#bbb', fontWeight: 600, flex: 1 }}>
              {followup.logs.length} change{followup.logs.length !== 1 ? 's' : ''} logged
            </Typography>
            <ExpandMoreIcon
              sx={{
                fontSize: 14, color: logsOpen ? TEAL : '#ccc',
                transition: 'transform 0.22s',
                transform: logsOpen ? 'rotate(180deg)' : 'none',
              }}
            />
          </Box>

          <Collapse in={logsOpen} timeout={220}>
            <Box sx={{ px: 2, pb: 1.25, pt: 0.75, bgcolor: `${TEAL}05`, borderTop: `1px solid ${TEAL_BORDER}` }}>
              <InlineHistory logs={followup.logs} />
              <NotesList logs={followup.logs} />
            </Box>
          </Collapse>
        </>
      )}
    </Paper>
  );
}

/* ── Shared section label ────────────────────────────────────────────────── */
function SectionLabel({ icon: Icon, title }) {
  return (
    <Box display="flex" alignItems="center" gap={0.75} mb={1}>
      <Icon sx={{ color: TEAL, fontSize: 15 }} />
      <Typography variant="caption" sx={{ color: TEAL, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: '0.68rem' }}>
        {title}
      </Typography>
    </Box>
  );
}

const selectSx = {
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e0e0e0' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: TEAL },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: TEAL, borderWidth: '2px' },
};
const textSx = {
  '& .MuiOutlinedInput-root': {
    '& fieldset': { borderColor: '#e0e0e0' },
    '&:hover fieldset': { borderColor: TEAL },
    '&.Mui-focused fieldset': { borderColor: TEAL, borderWidth: '2px' },
  },
  '& .MuiInputLabel-root.Mui-focused': { color: TEAL },
};

/* ── Edit Dialog ─────────────────────────────────────────────────────────── */
function EditFollowupDialog({ open, followup, onClose, onSaved, currentUserId }) {
  const [form, setForm] = useState({ probable_date: '', status: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const minDate = useMemo(() => dayjs().add(1, 'day'), []);

  useEffect(() => {
    if (open && followup) { setForm({ probable_date: followup.probable_date || '', status: followup.status || 'PENDING', notes: '' }); setError(''); }
  }, [open, followup]);

  const handleChange = (f, v) => setForm(p => ({ ...p, [f]: v }));

  const handleSave = async () => {
    setError(''); setSaving(true);
    try {
      const resp = await api.opd.updateFollowup(followup.id, {
        probable_date: form.probable_date || undefined,
        status: form.status || undefined,
        notes: form.notes || undefined,
        updated_by: currentUserId,
      });
      if (resp?.flag === 1 || resp?.statusCode === 200) {
        await swalToast('Updated', resp?.message || 'Follow-up updated successfully.');
        onSaved && onSaved(); onClose();
      } else { setError(resp?.message || 'Failed to update.'); }
    } catch (err) { setError(err?.response?.data?.message || err.message || 'Failed to update.'); }
    finally { setSaving(false); }
  };

  if (!followup) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
      <DialogTitle sx={{ bgcolor: TEAL, px: 3, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="h6" sx={{ color: '#fff !important', fontWeight: 600, fontSize: '1rem', lineHeight: 1.3 }}>Update Follow-Up</Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.72rem' }}>
            {resolvePatientName(followup.contactInfo, followup.irm_contacts_id)}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: '#fff' }}><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3, pt: '24px !important' }}>
        <Grid container spacing={2.5}>
          <Grid item xs={12}>
            <Box sx={{ bgcolor: TEAL_LIGHT, border: `1px solid ${TEAL_BORDER}`, borderRadius: 1.5, p: 1.5 }}>
              <Stack direction="row" spacing={3} flexWrap="wrap">
                <Box>
                  <Typography variant="caption" sx={{ color: '#aaa', fontSize: '0.64rem', textTransform: 'uppercase' }}>Current Date</Typography>
                  <Typography variant="body2" fontWeight={700} sx={{ color: TEAL }}>{fmt(followup.probable_date)}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: '#aaa', fontSize: '0.64rem', textTransform: 'uppercase' }}>Status</Typography>
                  <Box mt={0.25}>
                    <Chip label={STATUS_LABELS[followup.status]} size="small" sx={{ height: 19, fontSize: '0.62rem', fontWeight: 700, bgcolor: `${STATUS_HEX[followup.status]}18`, color: STATUS_HEX[followup.status], border: `1px solid ${STATUS_HEX[followup.status]}55` }} />
                  </Box>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: '#aaa', fontSize: '0.64rem', textTransform: 'uppercase' }}>Doctor</Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                    {resolveDoctorName(followup.doctorInfo) || `User #${followup.irm_users_id}`}
                    {resolveDoctorOccupation(followup.doctorInfo) && (
                      <span style={{ color: '#999', fontSize: '0.72rem', marginLeft: 4 }}>
                        ({resolveDoctorOccupation(followup.doctorInfo)})
                      </span>
                    )}
                  </Typography>
                </Box>
              </Stack>
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            <SectionLabel icon={CalendarMonthIcon} title="Reschedule Date" />
            <MDDatePicker label="New Probable Date" value={form.probable_date} onChange={d => handleChange('probable_date', d)} minDate={minDate} textFieldProps={{ fullWidth: true, size: 'small', sx: textSx }} />
          </Grid>

          <Grid item xs={12} sm={6}>
            <SectionLabel icon={EventIcon} title="Update Status" />
            <FormControl fullWidth size="small">
              <InputLabel id="edit-status-lbl" shrink sx={{ bgcolor: 'white', px: 0.5, '&.Mui-focused': { color: TEAL } }}>Status</InputLabel>
              <Select labelId="edit-status-lbl" label="Status" value={form.status} onChange={e => handleChange('status', e.target.value)} notched sx={{ ...selectSx, height: 40 }}>
                {STATUS_LIST.map(s => <MenuItem key={s} value={s} sx={{ color: STATUS_HEX[s], fontWeight: 500 }}>{STATUS_LABELS[s]}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <SectionLabel icon={NotesIcon} title="Notes / Remarks" />
            <RichTextEditor value={form.notes} onChange={v => handleChange('notes', v)} height="100px" placeholder="ADD NOTES ABOUT THIS UPDATE..." />
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 0.5 }} />
            <Typography variant="caption" sx={{ color: '#bbb', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700, fontSize: '0.62rem' }}>Audit History</Typography>
            <Box mt={0.75}><InlineHistory logs={followup.logs} /></Box>
            <Box mt={0.5}><NotesList logs={followup.logs} /></Box>
          </Grid>

          {error && <Grid item xs={12}><Alert severity="error" sx={{ py: 0.5 }}>{error}</Alert></Grid>}
        </Grid>
      </DialogContent>

      <Divider />
      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <MDButton variant="outlined" color="secondary" onClick={onClose} disabled={saving}>Cancel</MDButton>
        <MDButton variant="gradient" color="logo" onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Update Follow-Up'}</MDButton>
      </DialogActions>
    </Dialog>
  );
}

/* ── Add Note Dialog ─────────────────────────────────────────────────────── */
function AddNoteDialog({ open, followup, onClose, onSaved, currentUserId }) {
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { if (open) { setNote(''); setError(''); } }, [open]);

  const handleSave = async () => {
    if (!note.trim() || note === '<p><br></p>') { setError('Please enter a note.'); return; }
    setError(''); setSaving(true);
    try {
      const resp = await api.opd.updateFollowup(followup.id, { notes: note.trim(), updated_by: currentUserId });
      if (resp?.flag === 1 || resp?.statusCode === 200) {
        await swalToast('Note Added', 'Note has been saved successfully.');
        onSaved && onSaved(); onClose();
      } else { setError(resp?.message || 'Failed to add note.'); }
    } catch (err) { setError(err?.response?.data?.message || 'Failed to add note.'); }
    finally { setSaving(false); }
  };

  if (!followup) return null;
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
      <DialogTitle sx={{ bgcolor: TEAL, px: 3, py: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" sx={{ color: '#fff !important', fontWeight: 600, fontSize: '1rem' }}>Add Note</Typography>
        <IconButton onClick={onClose} size="small" sx={{ color: '#fff' }}><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: '20px !important' }}>
        <SectionLabel icon={NotesIcon} title="Note" />
        <RichTextEditor value={note} onChange={setNote} height="130px" placeholder="ENTER NOTE..." />
        {error && <Alert severity="error" sx={{ mt: 1.5, py: 0.5 }}>{error}</Alert>}
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <MDButton variant="outlined" color="secondary" onClick={onClose} disabled={saving}>Cancel</MDButton>
        <MDButton variant="gradient" color="logo" onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Add Note'}</MDButton>
      </DialogActions>
    </Dialog>
  );
}

/* ── Create Appointment (from a follow-up) ───────────────────────────────── */
function CreateAppointmentFromFollowupDialog({ open, followup, onClose, onCreated, currentUserId }) {
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Default to the follow-up's own probable_date, but never a past date —
  // an overdue follow-up defaults to today instead. User can still move the
  // date forward (never backward, past is blocked via minDate below).
  useEffect(() => {
    if (open && followup) {
      const todayStr = localDateStr(new Date());
      const followupDateStr = followup.probable_date ? String(followup.probable_date).slice(0, 10) : todayStr; // eslint-disable-line no-magic-numbers
      setDate(followupDateStr < todayStr ? todayStr : followupDateStr);
      setTime('');
    }
  }, [open, followup]);

  if (!followup) return null;

  const patientName = resolvePatientName(followup.contactInfo, followup.irm_contacts_id);
  const doctorName = resolveDoctorName(followup.doctorInfo);
  const minDate = dayjs();

  const handleSubmit = async () => {
    if (!date) { Swal.fire({ icon: 'warning', title: 'Select a date', timer: 1400, showConfirmButton: false }); return; }
    const res = await swalConfirm({
      title: 'Create Appointment?',
      html: `<b>${patientName}</b>${doctorName ? ` with <b>${doctorName}</b>` : ''} on <b>${fmt(date)}</b>${time ? ` at <b>${time}</b>` : ''}`,
      confirmText: 'Yes, Create',
    });
    if (!res.isConfirmed) return;
    setSubmitting(true);
    try {
      const resp = await api.appointment.createAppointment({
        irm_contacts_id: followup.irm_contacts_id,
        irm_users_id: followup.irm_users_id,
        appointment_date: date,
        appointment_time: time ? dayjs(time, 'hh:mm A').format('HH:mm') : undefined,
        reason_for_visit: followup.reason ? stripHtml(followup.reason) : undefined,
        created_by: currentUserId,
      });
      if (resp?.flag === 1 || resp?.statusCode === 200 || resp?.statusCode === 201) {
        await swalToast('Appointment Created', `Appointment scheduled for ${fmt(date)}.`);
        onCreated && onCreated();
        onClose();
      }
    } catch { /* ApiClient already shows a generic error toast */ }
    finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
      <DialogTitle sx={{ bgcolor: TEAL, px: 3, py: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h6" sx={{ color: '#fff !important', fontWeight: 600, fontSize: '1rem' }}>Create Appointment</Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.72rem' }}>{patientName}</Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: '#fff' }}><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: '20px !important' }}>
        <Box sx={{ bgcolor: TEAL_LIGHT, border: `1px solid ${TEAL_BORDER}`, borderRadius: 1.5, p: 1.5, mb: 2 }}>
          <Typography variant="caption" sx={{ color: '#aaa', fontSize: '0.64rem', textTransform: 'uppercase' }}>Doctor</Typography>
          <Typography variant="body2" fontWeight={700} sx={{ color: TEAL }}>{doctorName || `User #${followup.irm_users_id}`}</Typography>
        </Box>

        <SectionLabel icon={CalendarMonthIcon} title="Appointment Date" />
        <MDDatePicker
          label="Appointment Date"
          value={date}
          onChange={d => setDate(d)}
          minDate={minDate}
          textFieldProps={{ fullWidth: true, size: 'small', sx: { ...textSx, mb: 2 } }}
        />

        <SectionLabel icon={EventIcon} title="Appointment Time (optional)" />
        <MDTimePicker
          label="Time"
          value={time}
          onChange={val => setTime(val || '')}
          minutesStep={5}
          textFieldProps={{ fullWidth: true, size: 'small', sx: textSx }}
        />
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <MDButton variant="outlined" color="secondary" onClick={onClose} disabled={submitting}>Cancel</MDButton>
        <MDButton variant="gradient" color="logo" onClick={handleSubmit} disabled={submitting || !date}>{submitting ? 'Creating…' : 'Create Appointment'}</MDButton>
      </DialogActions>
    </Dialog>
  );
}

/* ── Full Detail Dialog ──────────────────────────────────────────────────── */
function FollowUpDetailDialog({ open, followup, onClose }) {
  if (!followup) return null;

  const patientName = resolvePatientName(followup.contactInfo, followup.irm_contacts_id);
  const phone = resolvePhone(followup.contactInfo);
  const doctorName = resolveDoctorName(followup.doctorInfo);
  const doctorOccupation = resolveDoctorOccupation(followup.doctorInfo);
  const statusColor = STATUS_HEX[followup.status] || TEAL;
  const historyLogs = (followup.logs || []).filter(l => l.change_type !== 'NOTE_ADDED');
  const noteLogs = (followup.logs || []).filter(l => l.change_type === 'NOTE_ADDED');

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
      <DialogTitle sx={{ bgcolor: TEAL, px: 3, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="h6" sx={{ color: '#fff !important', fontWeight: 600, fontSize: '1rem' }}>Follow-Up Details</Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.72rem' }}>{patientName}</Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: '#fff' }}><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3, pt: '24px !important' }}>
        <Grid container spacing={2.5}>

          {/* Patient + Doctor info */}
          <Grid item xs={12}>
            <Box sx={{ bgcolor: TEAL_LIGHT, border: `1px solid ${TEAL_BORDER}`, borderRadius: 1.5, p: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" sx={{ color: '#aaa', fontSize: '0.62rem', textTransform: 'uppercase', fontWeight: 700 }}>Patient</Typography>
                  <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.9rem', mt: 0.25 }}>{patientName}</Typography>
                  {phone && (
                    <Box display="flex" alignItems="center" gap={0.5} mt={0.25}>
                      <PhoneIcon sx={{ fontSize: 12, color: '#bbb' }} />
                      <Typography variant="caption" sx={{ color: '#666', fontSize: '0.75rem' }}>{phone}</Typography>
                    </Box>
                  )}
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" sx={{ color: '#aaa', fontSize: '0.62rem', textTransform: 'uppercase', fontWeight: 700 }}>Consulting Doctor</Typography>
                  <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.88rem', mt: 0.25 }}>
                    {doctorName || `User #${followup.irm_users_id}`}
                  </Typography>
                  {doctorOccupation && (
                    <Typography variant="caption" sx={{ color: '#888', fontSize: '0.72rem' }}>{doctorOccupation}</Typography>
                  )}
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" sx={{ color: '#aaa', fontSize: '0.62rem', textTransform: 'uppercase', fontWeight: 700 }}>Probable Date</Typography>
                  <Typography variant="body2" fontWeight={700} sx={{ color: TEAL, fontSize: '0.88rem', mt: 0.25 }}>{fmtFull(followup.probable_date)}</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" sx={{ color: '#aaa', fontSize: '0.62rem', textTransform: 'uppercase', fontWeight: 700 }}>Status</Typography>
                  <Box mt={0.5}>
                    <Chip label={STATUS_LABELS[followup.status]} size="small" sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700, bgcolor: `${statusColor}18`, color: statusColor, border: `1px solid ${statusColor}55` }} />
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Grid>

          {/* Reason */}
          {followup.reason && (
            <Grid item xs={12}>
              <Typography variant="caption" sx={{ color: '#aaa', fontSize: '0.62rem', textTransform: 'uppercase', fontWeight: 700 }}>Reason</Typography>
              <Typography variant="body2" sx={{ mt: 0.5, color: '#444', fontSize: '0.85rem' }}>{stripHtml(followup.reason)}</Typography>
            </Grid>
          )}

          {/* Treatment Line */}
          {followup.treatment_line && (
            <Grid item xs={12}>
              <Typography variant="caption" sx={{ color: '#aaa', fontSize: '0.62rem', textTransform: 'uppercase', fontWeight: 700 }}>Treatment Line</Typography>
              <Box sx={{ mt: 0.5, p: 1.5, bgcolor: '#f9f9f9', borderRadius: 1.5, border: '1px solid #eee' }}>
                <Typography variant="body2" sx={{ color: '#444', fontSize: '0.82rem', lineHeight: 1.6 }}>{stripHtml(followup.treatment_line)}</Typography>
              </Box>
            </Grid>
          )}

          {/* Remarks */}
          {followup.remarks && followup.remarks !== '<p><br></p>' && (
            <Grid item xs={12}>
              <Typography variant="caption" sx={{ color: '#aaa', fontSize: '0.62rem', textTransform: 'uppercase', fontWeight: 700 }}>Remarks</Typography>
              <Box
                sx={{ mt: 0.5, p: 1.5, bgcolor: '#f9f9f9', borderRadius: 1.5, border: '1px solid #eee', '& p': { margin: '2px 0', fontSize: '0.82rem', color: '#444' }, '& ul, & ol': { pl: 2, my: 0.5 } }}
                dangerouslySetInnerHTML={{ __html: followup.remarks }}
              />
            </Grid>
          )}

          {/* Change History */}
          {historyLogs.length > 0 && (
            <Grid item xs={12}>
              <Divider sx={{ mb: 1.5 }} />
              <Typography variant="caption" sx={{ color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700, fontSize: '0.62rem' }}>Change History</Typography>
              <Box mt={0.75} sx={{ pl: 1.5, borderLeft: `2px solid ${TEAL_BORDER}` }}>
                {historyLogs.map((log, i) => (
                  <Box key={log.id || i} display="flex" alignItems="flex-start" gap={1.5} sx={{ mb: 0.75 }}>
                    <Typography variant="caption" sx={{ color: '#bbb', whiteSpace: 'nowrap', fontSize: '0.67rem', minWidth: 82, mt: 0.25 }}>{fmt(log.created_at)}</Typography>
                    <Box>
                      <Chip label={log.change_type?.replace(/_/g, ' ')} size="small" sx={{ height: 17, fontSize: '0.58rem', bgcolor: `${TEAL}18`, color: TEAL, fontWeight: 700, mb: 0.25 }} />
                      <Typography variant="caption" sx={{ color: '#555', fontSize: '0.74rem', display: 'block' }}>
                        {log.change_type === 'DATE_CHANGED' && (
                          <><s style={{ color: '#ccc' }}>{fmt(log.old_date)}</s> → <strong>{fmt(log.new_date)}</strong></>
                        )}
                        {log.change_type === 'STATUS_CHANGED' && (
                          <><s style={{ color: '#ccc' }}>{log.old_status}</s> → <strong style={{ color: STATUS_HEX[log.new_status] }}>{log.new_status}</strong></>
                        )}
                        {log.change_type === 'CREATED' && (
                          <>Scheduled for <strong>{fmt(log.new_date)}</strong> · <strong>{log.new_status}</strong></>
                        )}
                        {log.notes && <span style={{ color: '#999', marginLeft: 6 }}>— {stripHtml(log.notes)}</span>}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Grid>
          )}

          {/* Notes */}
          {noteLogs.length > 0 && (
            <Grid item xs={12}>
              <Divider sx={{ mb: 1.5 }} />
              <Typography variant="caption" sx={{ color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700, fontSize: '0.62rem' }}>Notes</Typography>
              {noteLogs.map((n, i) => (
                <Box key={n.id || i} sx={{ mt: 0.75, p: 1.25, bgcolor: '#fffde7', borderRadius: 1.5, border: '1px solid #fff9c4' }}>
                  <Typography variant="caption" sx={{ color: '#bbb', fontSize: '0.64rem', display: 'block', mb: 0.25 }}>{fmt(n.created_at)}</Typography>
                  <Box
                    sx={{ '& p': { margin: '2px 0', fontSize: '0.8rem', color: '#444' } }}
                    dangerouslySetInnerHTML={{ __html: n.notes }}
                  />
                </Box>
              ))}
            </Grid>
          )}

        </Grid>
      </DialogContent>

      <Divider />
      <DialogActions sx={{ px: 3, py: 2 }}>
        <MDButton variant="outlined" color="secondary" onClick={onClose}>Close</MDButton>
      </DialogActions>
    </Dialog>
  );
}

/* ── Main Screen ─────────────────────────────────────────────────────────── */
function FollowUps() {
  const [startDate, setStartDate] = useState(offsetDate(-2));
  const [endDate, setEndDate] = useState(offsetDate(2));
  const [statusFilter, setStatusFilter] = useState('');
  const [followups, setFollowups] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [editTarget, setEditTarget] = useState(null);
  const [noteTarget, setNoteTarget] = useState(null);
  const [nextFollowUpSource, setNextFollowUpSource] = useState(null);
  const [createApptSource, setCreateApptSource] = useState(null);
  const [detailTarget, setDetailTarget] = useState(null);
  const [includeClosed, setIncludeClosed] = useState(false);
  const [exporting, setExporting] = useState(false);

  const currentUserId = (() => {
    try { const raw = localStorage.getItem('user') || localStorage.getItem('userData'); if (raw) { const p = JSON.parse(raw); return p?.user_id || p?.id || 1; } } catch { /**/ } return 1;
  })();

  const loadFollowups = useCallback(async (pg, rpp) => {
    const _pg = pg ?? page; const _rpp = rpp ?? rowsPerPage;
    if (!startDate || !endDate) return;
    setLoading(true); setError('');
    try {
      const params = { start_date: startDate, end_date: endDate, limit: _rpp, offset: _pg * _rpp };
      if (statusFilter) params.status = statusFilter;
      const resp = await api.opd.adminListFollowups(params);
      if (resp?.flag === 1 || resp?.statusCode === 200) {
        setFollowups(resp.data || []);
        setTotal(resp.total ?? resp.data?.length ?? 0);
      } else { setError(resp?.message || 'Failed to load follow-ups.'); }
    } catch (err) { setError(err?.response?.data?.message || err.message || 'Failed to load follow-ups.'); }
    finally { setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, statusFilter, page, rowsPerPage]);

  useEffect(() => { loadFollowups(page, rowsPerPage); }, [startDate, endDate, statusFilter, page, rowsPerPage]); // eslint-disable-line

  const handlePageChange = (_, np) => setPage(np);
  const handleRowsPerPageChange = e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); };
  const handleFilterChange = (setter, val) => { setter(val); setPage(0); };
  const handleRefresh = useCallback(() => loadFollowups(page, rowsPerPage), [loadFollowups, page, rowsPerPage]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = { start_date: startDate, end_date: endDate, limit: 5000, offset: 0 };
      if (statusFilter) params.status = statusFilter;
      const resp = await api.opd.adminListFollowups(params);
      if (resp?.flag === 1 || resp?.statusCode === 200) {
        const rows = (resp.data || []).map(f => ({
          'Patient Name': resolvePatientName(f.contactInfo, f.irm_contacts_id),
          'Phone': resolvePhone(f.contactInfo) || '',
          'Doctor': resolveDoctorName(f.doctorInfo) || '',
          'Qualification': resolveDoctorOccupation(f.doctorInfo) || '',
          'Probable Date': f.probable_date || '',
          'Status': STATUS_LABELS[f.status] || f.status || '',
          'Reason': stripHtml(f.reason) || '',
          'Treatment Line': stripHtml(f.treatment_line) || '',
          'Remarks': stripHtml(f.remarks) || '',
          'Notes': (f.logs || []).filter(l => l.change_type === 'NOTE_ADDED').map(l => stripHtml(l.notes)).join(' | ') || '',
          'Created At': f.created_at ? new Date(f.created_at).toLocaleDateString('en-IN') : '',
        }));
        const XLSX = await import('xlsx');
        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet(rows);
        ws['!cols'] = [
          { wch: 26 }, { wch: 15 }, { wch: 22 }, { wch: 22 },
          { wch: 14 }, { wch: 13 }, { wch: 32 }, { wch: 40 },
          { wch: 40 }, { wch: 40 }, { wch: 14 },
        ];
        XLSX.utils.book_append_sheet(wb, ws, 'Follow-Ups');
        const label = viewAllMode ? 'All' : `${startDate}_to_${endDate}`;
        XLSX.writeFile(wb, `FollowUps_${label}.xlsx`);
      } else {
        Swal.fire({ icon: 'error', title: 'Export Failed', text: resp?.message || 'Could not fetch data.', timer: 2400, showConfirmButton: false });
      }
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Export Failed', text: err.message || 'Unknown error.', timer: 2400, showConfirmButton: false });
    } finally {
      setExporting(false);
    }
  };

  const visibleFollowups = useMemo(
    () => includeClosed ? followups : followups.filter(f => f.status !== 'CLOSED'),
    [followups, includeClosed],
  );

  const grouped = useMemo(() => {
    const acc = {};
    visibleFollowups.forEach(f => { const d = f.probable_date || 'Unknown'; if (!acc[d]) acc[d] = []; acc[d].push(f); });
    return acc;
  }, [visibleFollowups]);
  const sortedDays = useMemo(() => Object.keys(grouped).sort(), [grouped]);

  const filterSx = {
    '& .MuiOutlinedInput-root': {
      height: 40,
      '& fieldset': { borderColor: '#e0e0e0' },
      '&:hover fieldset': { borderColor: TEAL },
      '&.Mui-focused fieldset': { borderColor: TEAL },
    },
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <LoaderOverlay open={loading} />

      <MDBox pt={6} pb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <MDBox mx={2} mt={-3} py={2} px={2} variant="gradient" bgColor="logo" borderRadius="lg" coloredShadow="logo">
                <MDTypography variant="h6" color="white">Follow-Ups</MDTypography>
              </MDBox>

              <MDBox p={3}>
                {/* ── Filters ── */}
                <Box
                  sx={{
                    display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', mb: 2,
                    p: 1.75, bgcolor: TEAL_LIGHT, borderRadius: 2, border: `1px solid ${TEAL_BORDER}`,
                  }}
                >
                  {/* From Date */}
                  <Box sx={{ minWidth: 155, flex: '1 1 155px' }}>
                    <MDDatePicker
                      label="From Date"
                      value={startDate}
                      onChange={d => handleFilterChange(setStartDate, d)}
                      textFieldProps={{
                        fullWidth: true,
                        size: 'small',
                        sx: filterSx,
                      }}
                    />
                  </Box>

                  {/* To Date */}
                  <Box sx={{ minWidth: 155, flex: '1 1 155px' }}>
                    <MDDatePicker
                      label="To Date"
                      value={endDate}
                      onChange={d => handleFilterChange(setEndDate, d)}
                      textFieldProps={{
                        fullWidth: true,
                        size: 'small',
                        sx: filterSx,
                      }}
                    />
                  </Box>

                  {/* Status */}
                  <Box sx={{ minWidth: 160, flex: '1 1 160px' }}>
                    <FormControl fullWidth size="small">
                      <InputLabel id="sf-lbl" shrink sx={{ bgcolor: 'white', px: 0.5, '&.Mui-focused': { color: TEAL } }}>Status</InputLabel>
                      <Select
                        labelId="sf-lbl" label="Status" value={statusFilter} notched displayEmpty
                        onChange={e => handleFilterChange(setStatusFilter, e.target.value)}
                        sx={{ ...selectSx, height: 40 }}
                      >
                        <MenuItem value="">All</MenuItem>
                        {STATUS_LIST
                          .filter(s => includeClosed || s !== 'CLOSED')
                          .map(s => (
                            <MenuItem key={s} value={s} sx={{ color: STATUS_HEX[s], fontWeight: 500 }}>
                              {STATUS_LABELS[s]}
                            </MenuItem>
                          ))}
                      </Select>
                    </FormControl>
                  </Box>

                  {/* Include Closed toggle */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0, bgcolor: 'white', borderRadius: 1.5, px: 1, height: 40, border: '1px solid #e0e0e0' }}>
                    <Switch
                      checked={includeClosed}
                      onChange={e => {
                        setIncludeClosed(e.target.checked);
                        if (!e.target.checked && statusFilter === 'CLOSED') setStatusFilter('');
                      }}
                      size="small"
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': { color: '#d32f2f' },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#d32f2f55' },
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{ fontSize: '0.71rem', fontWeight: 600, whiteSpace: 'nowrap', color: includeClosed ? '#d32f2f' : '#aaa', userSelect: 'none', cursor: 'default' }}
                    >
                      Include Closed
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                    {/* Refresh */}
                    <MDButton
                      variant="gradient" color="logo"
                      onClick={handleRefresh} disabled={loading}
                      sx={{ height: 40, px: 2.5, fontSize: '0.78rem' }}
                    >
                      {loading ? '…' : 'Refresh'}
                    </MDButton>

                    {/* Export Excel */}
                    <Button
                      variant="outlined"
                      onClick={handleExport}
                      disabled={exporting || loading || followups.length === 0}
                      startIcon={<FileDownloadIcon sx={{ fontSize: '15px !important' }} />}
                      sx={{
                        height: 40, px: 2, fontSize: '0.72rem', fontWeight: 600, textTransform: 'none',
                        borderColor: '#2e7d32', color: '#2e7d32',
                        '&:hover': { bgcolor: '#2e7d3210', borderColor: '#2e7d32' },
                        '&:disabled': { borderColor: '#ccc', color: '#bbb' },
                      }}
                    >
                      {exporting ? 'Exporting…' : 'Export Excel'}
                    </Button>
                  </Box>
                </Box>

                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                {total > 0 && (
                  <Typography variant="caption" sx={{ color: '#aaa', mb: 1.5, display: 'block' }}>
                    Showing {visibleFollowups.length}{visibleFollowups.length !== followups.length ? ` of ${followups.length}` : ''} follow-up{total !== 1 ? 's' : ''}{total > followups.length ? ` (${total} total)` : ''}
                  </Typography>
                )}

                {!loading && followups.length === 0 && !error && (
                  <Box textAlign="center" py={6}>
                    <EventIcon sx={{ fontSize: 44, color: '#e0e0e0', display: 'block', mx: 'auto', mb: 1 }} />
                    <Typography variant="body2" color="textSecondary">No follow-ups found for the selected date range.</Typography>
                  </Box>
                )}

                {sortedDays.map(day => (
                  <Box key={day} mb={2}>
                    <Box display="flex" alignItems="center" gap={1} mb={0.75} py={0.5} px={1.5}
                      sx={{ bgcolor: TEAL_LIGHT, borderRadius: 1, border: `1px solid ${TEAL_BORDER}` }}>
                      <EventIcon sx={{ color: TEAL, fontSize: 14 }} />
                      <Typography variant="body2" fontWeight={700} sx={{ color: TEAL, fontSize: '0.77rem' }}>{fmtFull(day)}</Typography>
                      <Chip label={grouped[day].length} size="small"
                        sx={{ height: 17, fontSize: '0.6rem', bgcolor: TEAL, color: 'white', ml: 'auto', '& .MuiChip-label': { px: 0.75 } }} />
                    </Box>
                    {grouped[day].map(f => (
                      <FollowupRow key={f.id} followup={f}
                        onEdit={setEditTarget} onAddNote={setNoteTarget}
                        onNextFollowUp={setNextFollowUpSource}
                        onViewDetails={setDetailTarget}
                        onCreateAppointment={setCreateApptSource}
                        onRefresh={handleRefresh} currentUserId={currentUserId}
                      />
                    ))}
                  </Box>
                ))}

                {total > 0 && (
                  <>
                    <Divider sx={{ mt: 1 }} />
                    <TablePagination component="div" count={total} page={page} rowsPerPage={rowsPerPage}
                      rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                      onPageChange={handlePageChange} onRowsPerPageChange={handleRowsPerPageChange}
                      labelRowsPerPage="Per page:"
                      sx={{
                        '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontSize: '0.75rem', color: '#888' },
                        '& .MuiTablePagination-actions button': { color: TEAL },
                      }}
                    />
                  </>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <Footer />

      <EditFollowupDialog open={Boolean(editTarget)} followup={editTarget}
        onClose={() => setEditTarget(null)} onSaved={handleRefresh} currentUserId={currentUserId} />
      <AddNoteDialog open={Boolean(noteTarget)} followup={noteTarget}
        onClose={() => setNoteTarget(null)} onSaved={handleRefresh} currentUserId={currentUserId} />
      <FollowUpDialog
        open={Boolean(nextFollowUpSource)}
        onClose={() => setNextFollowUpSource(null)}
        onSuccess={() => { setNextFollowUpSource(null); handleRefresh(); }}
        contactId={nextFollowUpSource?.irm_contacts_id}
        defaultDoctorId={nextFollowUpSource?.irm_users_id}
        prescriptionId={nextFollowUpSource?.irm_prescriptions_id || null}
        treatmentLine={nextFollowUpSource ? stripHtml(nextFollowUpSource.treatment_line) : null}
        currentUserId={currentUserId}
      />
      <FollowUpDetailDialog
        open={Boolean(detailTarget)}
        followup={detailTarget}
        onClose={() => setDetailTarget(null)}
      />
      <CreateAppointmentFromFollowupDialog
        open={Boolean(createApptSource)}
        followup={createApptSource}
        onClose={() => setCreateApptSource(null)}
        onCreated={handleRefresh}
        currentUserId={currentUserId}
      />
    </DashboardLayout>
  );
}

export default FollowUps;
